import * as React from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import {
  Alert,
  Button,
  darkTheme,
  Grid,
  MultiThemeProvider,
  Snackbar,
  Typography,
} from '@mineral/core';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Router } from 'components/Router';
import './styles.css';
import { ToastContainer } from 'react-toastify';
import { Security } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { theme, themeProps } from 'Utils/style/theme';
import { useICE } from '@mineral-community/ice-utils';
import { createContext } from 'react';
import { NetworkErrorEnum, ProgressStatus } from 'components/Enums';
import RoutedBreadcrumbContextProvider from 'api/ApplicationContext/RoutedBreadcrumbContext';
import { getApiDefinition } from 'api/ApplicationContext/AppContext';
import { IAuth } from 'types/IAuth';
import { RefreshPopup } from 'components/RefreshPopup';
import { IRBACTabs } from 'types/IRBAC';

interface IPCECard {
  type: string;
  'panel-title': string;
  'panel-description': string;
  'icon-type': string;
  icon?: string;
  ipceCardLoaded?: (e: Event) => void;
  [dynamicString: string]: unknown;
}
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'ipce-card': IPCECard;
      'ipce-illustrations': { illustration: string | React.ReactNode };
    }
  }
}

const parseBool = function (str: string) {
  if (typeof str === 'string' && str.toLowerCase() === 'true') {
    return true;
  }

  return parseInt(str) > 0;
};
const oktaAuth = new OktaAuth({
  clientId: process.env.REACT_APP_CLIENT_ID || '',
  issuer: process.env.REACT_APP_ISSUER || '',
  redirectUri: process.env.REACT_APP_REDIRECT_URI || '',
  scopes: process.env.REACT_APP_SCOPES?.split(',') || [],
  pkce: parseBool(process.env.REACT_APP_PKCE || ''),
  clientSecret: process.env.REACT_APP_CLIENT_SECRET || '',
  postLogoutRedirectUri: process.env.REACT_APP_LOGOUT_REDIRECT_URL || '',
});
const queryClient = new QueryClient();

export const QueryClientApp: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <Router />
      </ErrorBoundary>
      <ReactQueryDevtools position="bottom-right" />
    </QueryClientProvider>
  );
};
export const AllowedFields = createContext<string | IRBACTabs>('*');
export const FullPermission = createContext<boolean>(true);
export const ICEInitialize = createContext<() => void>(() => {});
const defaultFields = process.env.NODE_ENV === 'development' ? '*' : '';

export const AppPlain: React.FC = () => {
  const navigate = useNavigate();
  const ICE_DEV_KEY = process.env.REACT_APP_ICE_KEY || ''; // Your Product Key
  const ice = useICE(ICE_DEV_KEY);
  const [sessionExpired, setSessionExpired] = React.useState(false);
  const [accessModified, setAccessModified] = React.useState(false);
  const location = useLocation();
  const [allowedFields, setAllowedFields] = React.useState<string | IRBACTabs>(
    defaultFields
  );
  const [hasFullPermissions, setHasFullPermissions] =
    React.useState<boolean>(true);

  React.useEffect(() => {
    if (
      localStorage.getItem('authPages') &&
      localStorage.getItem('authPages') !== 'undefined'
    ) {
      const allowedFields = localStorage.getItem('authPages');
      if (allowedFields) {
        if (allowedFields.includes(defaultFields)) {
          setAllowedFields(defaultFields);
        } else {
          setAllowedFields(JSON.parse(allowedFields) as IRBACTabs);
        }
      } else {
        setAllowedFields(defaultFields);
      }
    }
  }, []);

  const navigatetoURI = (originalUri?: string) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  const navigateNotFound = React.useCallback(() => {
    navigate('/403');
  }, [navigate]);
  window.addEventListener('accessModified', () => {
    setAccessModified(true);
  });
  window.addEventListener('sessionExpired', () => {
    setSessionExpired(true);
  });

  const updatedKey = async (
    navigateNotFound?: () => void,
    isPopupRequired?: boolean,
    originalUri?: string,
    navigatetoURI?: (originalUri?: string) => void
  ) => {
    const apidef = getApiDefinition('auth');
    const accessToken = await oktaAuth.tokenManager.get('accessToken');
    const headerObj = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    const authObj = {
      authorization: `Bearer ${accessToken?.accessToken}`,
    };

    if (
      accessToken &&
      accessToken.accessToken &&
      !oktaAuth.tokenManager.hasExpired(accessToken)
    ) {
      void fetch(apidef.Uri, {
        headers: new Headers({ ...headerObj, ...authObj }),
      })
        .then((res) => res.json())
        .then((output) => {
          if (output) {
            if (output.status === 'error') {
              navigateNotFound && navigateNotFound();
            } else {
              const data = output as IAuth;
              localStorage.setItem('authApiKey', data.apiKey);
              localStorage.setItem(
                'authPages',
                process.env.NODE_ENV === 'development'
                  ? "'*'"
                  : JSON.stringify(data.menuOptions)
              );
              setAllowedFields(
                typeof data.menuOptions === 'string' ? '*' : data.menuOptions
              );
              setHasFullPermissions(Boolean(data.hasFullAccess));
              if (isPopupRequired) {
                setSessionExpired(true);
              }
              setTimeout(() => {
                if (
                  localStorage.getItem('authPages') !== null &&
                  localStorage.getItem('authApiKey') !== null
                ) {
                  navigatetoURI && navigatetoURI(originalUri);
                }
              }, 100);
            }
          }
        })
        .catch(() => {
          navigateNotFound && navigateNotFound();
        });
    }
  };

  React.useEffect(() => {
    if (
      process.env.NODE_ENV !== 'development' &&
      location.pathname !== '/logout'
    ) {
      void updatedKey(navigateNotFound);
    }
  }, [location.pathname, navigateNotFound]);
  React.useEffect(() => {
    const handleTokenChanged = () => {
      if (location.pathname !== '/logout') {
        void updatedKey(navigateNotFound);
      }
    };

    oktaAuth.tokenManager.on('renewed', handleTokenChanged);

    return () => {
      oktaAuth.tokenManager.off('renewed', handleTokenChanged);
      window.removeEventListener('accessModified', () => {});
      window.removeEventListener('sessionExpired', () => {});
    };
  }, [location.pathname, navigateNotFound]);
  const iceLaunchedRef = React.useRef<boolean>(false);
  const initializeICE = React.useCallback(
    (mode: 'off' | 'overlay' | 'docked' | undefined = 'overlay') => {
      iceLaunchedRef.current = Boolean(
        ice?.init?.(
          {
            name: process.env.REACT_APP_ICE_NAME || '', // Your Product Identifier
            version: '0.0.1', // Your Product Version (leave empty if you want to support multiple versions)
            displayName: process.env.REACT_APP_ICE_DISPLAY_NAME || '',
          },
          {
            userName: '', // Optional
            email: '', // Optional
          },
          {
            mode: mode,
          }
        )
      );
    },
    [ice]
  );

  React.useEffect(() => {
    initializeICE('off');
  }, [initializeICE]);

  if (process.env.NODE_ENV !== 'development') {
    const restoreOriginalUri = (_oktaAuth: unknown, originalUri: string) => {
      if (originalUri !== '/logout') {
        void updatedKey(navigateNotFound, false, originalUri, navigatetoURI);
      } else {
        navigatetoURI(originalUri);
      }
    };

    return (
      <HelmetProvider>
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
          <AllowedFields.Provider value={allowedFields}>
            <FullPermission.Provider value={hasFullPermissions}>
              <ICEInitialize.Provider value={initializeICE}>
                {(sessionExpired || accessModified) && (
                  <RefreshPopup isSessionExpired={!accessModified} />
                )}
                <QueryClientApp />
              </ICEInitialize.Provider>
            </FullPermission.Provider>
          </AllowedFields.Provider>
        </Security>
      </HelmetProvider>
    );
  } else {
    return (
      <HelmetProvider>
        <AllowedFields.Provider value={allowedFields}>
          <FullPermission.Provider value={hasFullPermissions}>
            <ICEInitialize.Provider value={initializeICE}>
              {(sessionExpired || accessModified) && (
                <RefreshPopup isSessionExpired={!accessModified} />
              )}
              <QueryClientApp />
            </ICEInitialize.Provider>
          </FullPermission.Provider>
        </AllowedFields.Provider>
      </HelmetProvider>
    );
  }
};

export const ProgressStateUpdate = createContext<
  (value: ProgressStatus) => void
>(() => {});

export const ProgressState = createContext<ProgressStatus>(ProgressStatus.none);
export const App: React.FC = () => {
  const [online, setOnline] = React.useState(false);
  window.addEventListener('offline', () => {
    setOnline(true);
  });
  window.addEventListener('online', () => {
    setOnline(false);
  });
  const [status, setStatus] = React.useState(ProgressStatus.none);

  return (
    <RoutedBreadcrumbContextProvider>
      <ProgressStateUpdate.Provider
        value={(val: ProgressStatus) => {
          setStatus((oldVal) => (oldVal === val ? oldVal : val));
        }}>
        <ProgressState.Provider value={status}>
          <BrowserRouter basename={process.env.PUBLIC_URL}>
            <MultiThemeProvider
              themes={{
                light: theme,
                dark: {
                  ...darkTheme,
                  ...themeProps,
                },
                default: theme,
              }}>
              <AppPlain />
              {online && (
                <Snackbar
                  anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
                  open>
                  <Alert
                    severity="error"
                    sx={(theme) => ({ minWidth: theme.spacing(76) })}>
                    <Grid container>
                      <Grid item>
                        <Typography variant="body2">
                          {NetworkErrorEnum.message}
                        </Typography>
                      </Grid>
                      <Grid item mt="-1%">
                        <Button
                          variant="contained"
                          size="medium"
                          sx={(theme) => ({ ml: theme.spacing(1) })}
                          onClick={() => {
                            window.location.reload();
                          }}>
                          Try again
                        </Button>
                      </Grid>
                    </Grid>
                  </Alert>
                </Snackbar>
              )}
              <ToastContainer />
            </MultiThemeProvider>
          </BrowserRouter>
        </ProgressState.Provider>
      </ProgressStateUpdate.Provider>
    </RoutedBreadcrumbContextProvider>
  );
};
